import { Dispatch, SetStateAction, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

export const useSafeLocalStorage = <T>(
  key: string,
  initialValue: T,
  shouldUseLocalStorage?: boolean
): [T, Dispatch<SetStateAction<T>>] => {
  const cleanLocalStorage = () => {
    Object.keys(globalThis.localStorage).forEach((localStorageName) => {
      if (localStorageName.startsWith('nps-survey') && localStorageName.endsWith('-createdAt')) {
        const localStorageCreatedAt = globalThis.localStorage.getItem(localStorageName);
        const localStorageKey = localStorageName.replace('-createdAt', '');
        if (
          localStorageCreatedAt &&
          new Date(localStorageCreatedAt) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        ) {
          globalThis.localStorage.removeItem(localStorageKey);
          globalThis.localStorage.removeItem(localStorageName);
        }
      }
    });
  };

  const testAndSetTimestampWasSuccessful = (isRetry?: boolean): boolean => {
    try {
      const testKey = '__test__';
      globalThis.localStorage.setItem(testKey, '__test__');
      globalThis.localStorage.removeItem(testKey);

      const timeStamp = new Date().toISOString();

      if (!globalThis.localStorage.getItem(`${key}-createdAt`)) {
        globalThis.localStorage.setItem(`${key}-createdAt`, timeStamp);
      }
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError' && !isRetry) {
        console.warn(
          'QuotaExceededError: Local storage is full. Falling back to in-memory storage.'
        );
        cleanLocalStorage();
        testAndSetTimestampWasSuccessful(true);
      }
      return false;
    }
  };

  const isLocalStorageAvailable = (() => {
    return Boolean(shouldUseLocalStorage) && testAndSetTimestampWasSuccessful();
  })();

  const [localStorageValue, setLocalStorageValue] = useLocalStorage<T>(key, initialValue);
  const [fallbackValue, setFallbackValue] = useState<T>(initialValue);

  if (isLocalStorageAvailable) {
    return [localStorageValue, setLocalStorageValue];
  }

  console.warn(
    `localStorage is unavailable or not allowed. Using fallback state for key "${key}".`
  );
  globalThis.localStorage.removeItem(key);
  globalThis.localStorage.removeItem(`${key}-createdAt`);
  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    const valueToStore =
      typeof value === 'function' ? (value as (prev: T) => T)(fallbackValue) : value;
    setFallbackValue(valueToStore);
  };
  return [fallbackValue, setValue];
};
